import { useCallback, useMemo } from 'react'

import _ from 'lodash'
import { useSelector } from 'react-redux'
import { isUserRoleApprover } from 'Components/utils/roles'
import { productState } from 'Config/constants'

const useOrderMin = () => {
  const user = useSelector(({ auth }) => auth.user)
  const center = useSelector(state => state.auth.selectedCenter)
  const cart = useSelector(state => state.purchase.cart)
  const { showPrice, requiresApproval } = center

  const [okProducts] = _.map(cart)?.reduce(
    ([ok, notOk], elem) => (elem.state === productState.noAvailable ? [ok, [...notOk, elem]] : [[...ok, elem], notOk]),
    [[], []]
  )

  const productsToShow = _.groupBy(okProducts, 'provider.name')

  const totalByProvider = useMemo(
    () =>
      _(okProducts)
        .groupBy('provider.name')
        .map((item, key) => ({
          index: key,
          total: _.sumBy(item, 'total'),
        }))
        .value(),
    [okProducts]
  )
  const minOrderByProvider = useMemo(() => {
    const providerMap = okProducts.reduce((acc, { provider }) => {
      acc[provider.name] = { orderMinimum: provider?.orderMinimum || null }
      return acc
    }, {})
    return providerMap
  }, [okProducts])

  const notMin = useMemo(() => {
    const result = Object.entries(minOrderByProvider).reduce((acc, [provider, data]) => {
      const totalItem = totalByProvider.find(tP => tP.index === provider)
      const total = totalItem?.total ?? 0
      const { orderMinimum } = data
      if (orderMinimum != null && orderMinimum - total > 0) {
        acc[provider] = {
          ...data,
          needsMore: orderMinimum - total,
        }
      }
      return acc
    }, {})
    return Object.keys(result)?.length > 0
  }, [minOrderByProvider, totalByProvider])

  const hastToShowErrorMinOrder = useMemo(() => {
    if (showPrice && notMin && !requiresApproval) {
      return true
    }
    return false
  }, [notMin, requiresApproval, showPrice])

  const getIfNeedMinByProvider = useCallback(
    prov =>
      showPrice &&
      !requiresApproval &&
      minOrderByProvider[prov]?.orderMinimum &&
      minOrderByProvider[prov]?.orderMinimum - totalByProvider.find(element => element.index === prov).total > 0,
    [minOrderByProvider, requiresApproval, showPrice, totalByProvider]
  )

  // if needs show message but not block purchase
  const getShowMinWithoutBlockPurchase = useCallback(
    prov => {
      const isApprover = isUserRoleApprover(user)
      return (
        !showPrice &&
        isApprover &&
        minOrderByProvider[prov]?.orderMinimum &&
        minOrderByProvider[prov]?.orderMinimum - totalByProvider.find(element => element.index === prov).total > 0
      )
    },
    [minOrderByProvider, showPrice, totalByProvider, user]
  )

  return {
    notMin,
    minOrderByProvider,
    totalByProvider,
    productsToShow,
    hastToShowErrorMinOrder,
    getIfNeedMinByProvider,
    getShowMinWithoutBlockPurchase,
  }
}

export default useOrderMin
